// @ts-ignore
import { useLocale } from "@libraries/web-framework";

import {
    checkFeatureFlagGate,
    featureFlags
} from "@/utils/feature-flags/index.ts";
import { getCurrencyFromUserLocale } from "@/utils/useGeoCurrency.ts";

import * as bitbucketCfg from "./configs/bitbucket.pricing-calculator-config.ts";
import * as compassCfg from "./configs/compass.pricing-calculator-config.ts";
import * as confluenceCfg from "./configs/confluence.pricing-calculator-config.ts";
import * as guardCfg from "./configs/guard.pricing-calculator-config.ts";
import * as jiraCfg from "./configs/jira.pricing-calculator-config.ts";
import * as jpdCfg from "./configs/jpd.pricing-calculator-config.ts";
import * as loomCfg from "./configs/loom.pricing-calculator-config.ts";
import * as rovoDevCfg from "./configs/rovo-dev.pricing-calculator-config.ts";
import * as serviceCollectionCfg from "./configs/service-collection.pricing-calculator-config.ts";
import * as strategyCollectionCfg from "./configs/strategy-collection.pricing-calculator-config.ts";
import * as teamworkCollectionCfg from "./configs/teamwork-collection.pricing-calculator-config.ts";
import { useCcpPricingData } from "./hooks.ts";
import {
    CcpPricingData,
    InputValues,
    PricingCalculatorContextProviderProps
} from "./index.ts";
import { Plan } from "./types.ts";

export interface UserProviderProps {
    product: string;
    plans: Plan[];
    inputValues?: InputValues;
}

export type UseProviderPropsResult =
    | {
          status: "pending" | "error";
      }
    | {
          props: Omit<PricingCalculatorContextProviderProps, "children">;
          status: "ready";
      };

// eslint-disable-next-line complexity
export function useProviderProps(
    props: UserProviderProps
): UseProviderPropsResult {
    const { plans, product, inputValues } = props;

    const billingCycle = inputValues?.billingCycle;
    const unitCount = inputValues?.unitCount;

    const locale = useLocale();
    const currencyISOCode = getCurrencyFromUserLocale(locale);

    const ssrData = plans.reduce((acc, plan) => {
        return {
            ...acc,
            [plan.fields.sku]: plan.pricingPlan
        };
    }, {}) as CcpPricingData;

    const { data: ccpPricingData, status } = useCcpPricingData({
        offeringSkus: plans.map((plan) => plan.fields.sku),
        ssrData
    });

    if (status === "error") {
        return {
            status: "error"
        } as const;
    }

    if (status === "pending") {
        return {
            status: "pending"
        } as const;
    }

    if (product === "jira") {
        return {
            status: "ready",
            props: {
                inputValues: {
                    billingCycle:
                        billingCycle ?? jiraCfg.JIRA_DEFAULT_BILLING_CYCLE,
                    unitCount: unitCount ?? jiraCfg.JIRA_DEFAULT_UNIT_COUNT
                },
                config: {
                    ccpPricingData,
                    planLimitConfigs: jiraCfg.jiraPlanLimitConfigs,
                    unitMinEnterprise: jiraCfg.JIRA_MIN_ENTERPRISE_USER_COUNT,
                    currencyISOCode,
                    localeISOCode: locale,
                    billingCycleConfig: {
                        monthly: true,
                        annual: true
                    }
                }
            }
        };
    }
    if (product === "confluence") {
        return {
            status: "ready",
            props: {
                inputValues: {
                    billingCycle:
                        billingCycle ??
                        confluenceCfg.CONFLUENCE_DEFAULT_BILLING_CYCLE,
                    unitCount:
                        unitCount ?? confluenceCfg.CONFLUENCE_DEFAULT_UNIT_COUNT
                },
                config: {
                    ccpPricingData,
                    planLimitConfigs: confluenceCfg.confluencePlanLimitConfigs,
                    unitMinEnterprise:
                        confluenceCfg.CONFLUENCE_MIN_ENTERPRISE_USER_COUNT,
                    currencyISOCode,
                    localeISOCode: locale,
                    billingCycleConfig: {
                        monthly: true,
                        annual: true
                    }
                }
            }
        };
    }
    if (product === "teamwork") {
        return {
            status: "ready",
            props: {
                inputValues: {
                    billingCycle:
                        billingCycle ??
                        teamworkCollectionCfg.TEAMWORK_COLLECTION_DEFAULT_BILLING_CYCLE,
                    unitCount:
                        unitCount ??
                        teamworkCollectionCfg.TEAMWORK_COLLECTION_DEFAULT_UNIT_COUNT
                },
                config: {
                    ccpPricingData,
                    planLimitConfigs:
                        teamworkCollectionCfg.teamworkCollectionPlanLimitConfigs,
                    unitMinEnterprise:
                        teamworkCollectionCfg.TEAMWORK_COLLECTION_MIN_ENTERPRISE_USER_COUNT,
                    currencyISOCode,
                    localeISOCode: locale,
                    billingCycleConfig:
                        teamworkCollectionCfg.teamworkCollectionBillingCycleConfig
                }
            }
        };
    }
    if (product === "guard") {
        return {
            status: "ready",
            props: {
                inputValues: {
                    billingCycle:
                        billingCycle ?? guardCfg.GUARD_DEFAULT_BILLING_CYCLE,
                    unitCount: unitCount ?? guardCfg.GUARD_DEFAULT_UNIT_COUNT
                },
                config: {
                    ccpPricingData,
                    planLimitConfigs: guardCfg.guardPlanLimitConfigs,
                    unitMinEnterprise: guardCfg.GUARD_MIN_ENTERPRISE_USER_COUNT,
                    currencyISOCode,
                    localeISOCode: locale,
                    billingCycleConfig: guardCfg.guardBillingCycleConfig
                }
            }
        };
    }

    if (product === "bitbucket") {
        return {
            status: "ready",
            props: {
                inputValues: {
                    billingCycle:
                        billingCycle ??
                        bitbucketCfg.BITBUCKET_DEFAULT_BILLING_CYCLE,
                    unitCount:
                        unitCount ?? bitbucketCfg.BITBUCKET_DEFAULT_UNIT_COUNT
                },
                config: {
                    ccpPricingData,
                    planLimitConfigs: bitbucketCfg.bitbucketPlanLimitConfigs,
                    unitMinEnterprise:
                        bitbucketCfg.BITBUCKET_MIN_ENTERPRISE_USER_COUNT,
                    // bitbucket only supports USD
                    currencyISOCode: "USD",
                    localeISOCode: locale,
                    billingCycleConfig: bitbucketCfg.bitbucketBillingCycleConfig
                }
            }
        };
    }

    if (product === "strategy") {
        return {
            status: "ready",
            props: {
                inputValues: {
                    billingCycle:
                        billingCycle ??
                        strategyCollectionCfg.STRATEGY_COLLECTION_DEFAULT_BILLING_CYCLE,
                    unitCount:
                        unitCount ??
                        strategyCollectionCfg.STRATEGY_COLLECTION_DEFAULT_UNIT_COUNT
                },
                config: {
                    ccpPricingData,
                    planLimitConfigs:
                        strategyCollectionCfg.strategyCollectionPlanLimitConfigs,
                    unitMinEnterprise:
                        strategyCollectionCfg.STRATEGY_COLLECTION_MIN_ENTERPRISE_USER_COUNT,
                    currencyISOCode,
                    localeISOCode: locale,
                    billingCycleConfig:
                        strategyCollectionCfg.strategyCollectionBillingCycleConfig
                }
            }
        };
    }

    if (product === "compass") {
        return {
            status: "ready",
            props: {
                inputValues: {
                    billingCycle:
                        billingCycle ??
                        compassCfg.COMPASS_DEFAULT_BILLING_CYCLE,
                    unitCount:
                        unitCount ?? compassCfg.COMPASS_DEFAULT_UNIT_COUNT
                },
                config: {
                    ccpPricingData,
                    planLimitConfigs: compassCfg.compassPlanLimitConfigs,
                    // not sure why this field is necessary
                    unitMinEnterprise:
                        compassCfg.COMPASS_MIN_ENTERPRISE_USER_COUNT,
                    currencyISOCode,
                    localeISOCode: locale,
                    billingCycleConfig: compassCfg.compassBillingCycleConfig
                }
            }
        };
    }

    if (product === "service") {
        const signupLimit = checkFeatureFlagGate(
            featureFlags.gwp11138ServcoPricingIncreaseMaxUsers
        )
            ? 100_000
            : 20_000;
        const planLimitConfigs =
            serviceCollectionCfg.serviceCollectionPlanLimitConfigs.map(
                (plan) => {
                    return { ...plan, signupLimit: signupLimit };
                }
            );
        return {
            status: "ready",
            props: {
                inputValues: {
                    billingCycle:
                        billingCycle ??
                        serviceCollectionCfg.SERVICE_COLLECTION_DEFAULT_BILLING_CYCLE,
                    unitCount:
                        unitCount ??
                        serviceCollectionCfg.SERVICE_COLLECTION_DEFAULT_UNIT_COUNT
                },
                config: {
                    ccpPricingData,
                    planLimitConfigs: planLimitConfigs,
                    unitMinEnterprise:
                        serviceCollectionCfg.SERVICE_COLLECTION_MIN_ENTERPRISE_USER_COUNT,
                    currencyISOCode,
                    localeISOCode: locale,
                    billingCycleConfig:
                        serviceCollectionCfg.serviceCollectionBillingCycleConfig
                }
            }
        };
    }

    if (product === "rovo-dev") {
        return {
            status: "ready",
            props: {
                inputValues: {
                    billingCycle:
                        billingCycle ??
                        rovoDevCfg.ROVO_DEV_DEFAULT_BILLING_CYCLE,
                    unitCount:
                        unitCount ?? rovoDevCfg.ROVO_DEV_DEFAULT_UNIT_COUNT
                },
                config: {
                    ccpPricingData,
                    planLimitConfigs: rovoDevCfg.rovoDevPlanLimitConfigs,
                    unitMinEnterprise:
                        rovoDevCfg.ROVO_DEV_MIN_ENTERPRISE_USER_COUNT,
                    currencyISOCode,
                    localeISOCode: locale,
                    billingCycleConfig: rovoDevCfg.rovoDevBillingCycleConfig
                }
            }
        };
    }

    if (product === "loom") {
        return {
            status: "ready",
            props: {
                inputValues: {
                    billingCycle:
                        billingCycle ?? loomCfg.LOOM_DEFAULT_BILLING_CYCLE,
                    unitCount: unitCount ?? loomCfg.LOOM_DEFAULT_UNIT_COUNT
                },
                config: {
                    ccpPricingData,
                    planLimitConfigs: loomCfg.loomPlanLimitConfigs,
                    unitMinEnterprise: loomCfg.LOOM_MIN_ENTERPRISE_USER_COUNT,
                    currencyISOCode,
                    localeISOCode: locale,
                    billingCycleConfig: loomCfg.loomBillingCycleConfig
                }
            }
        };
    }

    if (product === "product-discovery") {
        return {
            status: "ready",
            props: {
                inputValues: {
                    billingCycle:
                        billingCycle ?? jpdCfg.JPD_DEFAULT_BILLING_CYCLE,
                    unitCount: unitCount ?? jpdCfg.JPD_DEFAULT_UNIT_COUNT
                },
                config: {
                    ccpPricingData,
                    planLimitConfigs: jpdCfg.jpdPlanLimitConfigs,
                    unitMinEnterprise: jpdCfg.JPD_MIN_ENTERPRISE_USER_COUNT,
                    currencyISOCode,
                    localeISOCode: locale,
                    billingCycleConfig: jpdCfg.jpdBillingCycleConfig
                }
            }
        };
    }

    throw new Error("unknown product");
}
